import envConfig from "@/config/envConfig";
import { MediaType } from "@/types/common";

const MEDIA_FILE_PATH = "/api/media/file/";

const SIZE_WIDTHS = {
  ld: 360,
  sd: 640,
  md: 900,
  hd: 1280,
  fullHD: 1920,
} as const;

type ArticleImageSize = keyof typeof SIZE_WIDTHS;

/** When image CDN base is the same origin as the API, media is served at /api/media/file/… (Payload), not at /{filename} on the API host. */
function shouldServeMediaViaPayloadApi(): boolean {
  const img = (envConfig.imgStoreUrl || "").replace(/\/+$/, "");
  const api = (envConfig.publicServerUrl || "").replace(/\/+$/, "");
  if (!img || !api) return true;
  try {
    return new URL(img).origin === new URL(api).origin;
  } catch {
    return img === api;
  }
}

function getCdnBaseUrl(): string {
  return (envConfig.imgStoreUrl || "").replace(/\/+$/, "");
}

/** Extract S3/storage key from a Payload media path or URL. */
export function extractMediaKey(raw: string): string {
  const trimmed = (raw || "").trim();
  if (!trimmed) return "";

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const { pathname } = new URL(trimmed);
      const fileMarker = pathname.indexOf(MEDIA_FILE_PATH);
      if (fileMarker !== -1) {
        return decodeURIComponent(pathname.slice(fileMarker + MEDIA_FILE_PATH.length));
      }

      const cdnBase = getCdnBaseUrl();
      if (cdnBase) {
        try {
          const cdnPath = new URL(cdnBase).pathname.replace(/\/$/, "");
          if (pathname.startsWith(cdnPath) && cdnPath !== "") {
            return decodeURIComponent(pathname.slice(cdnPath.length).replace(/^\//, ""));
          }
          if (trimmed.startsWith(`${cdnBase}/`)) {
            return decodeURIComponent(trimmed.slice(cdnBase.length + 1));
          }
        } catch {
          /* fall through */
        }
      }

      return decodeURIComponent(pathname.replace(/^\//, ""));
    } catch {
      return trimmed;
    }
  }

  if (trimmed.includes(MEDIA_FILE_PATH)) {
    const afterFile = trimmed.split("file/").at(1) ?? "";
    return decodeURIComponent(afterFile.replace(/^\/+/, ""));
  }

  if (trimmed.includes("/media/")) {
    return decodeURIComponent(trimmed.replace(/^\/?media\//, ""));
  }

  return decodeURIComponent(trimmed.replace(/^\/+/, ""));
}

/** Build backend URL for a stored media key (e.g. epaper-media/foo.jpg, image/bar.png). */
function buildPayloadMediaFileUrl(filename: string): string {
  const base = (envConfig.publicServerUrl || "").replace(/\/+$/, "");
  const parts = String(filename ?? "")
    .trim()
    .split("/")
    .filter(Boolean);
  if (!base || parts.length === 0) return "";
  const encoded = parts.map(p => encodeURIComponent(p)).join("/");
  return `${base}/api/media/file/${encoded}`;
}

function resolveMediaKeyToUrl(key: string): string {
  const cleanKey = extractMediaKey(key);
  if (!cleanKey) return "";

  if (shouldServeMediaViaPayloadApi()) {
    return buildPayloadMediaFileUrl(cleanKey);
  }

  const cleanImg = getCdnBaseUrl();
  return cleanImg ? `${cleanImg}/${cleanKey}` : buildPayloadMediaFileUrl(cleanKey);
}

/** Get display URL for user profile picture. Profile pics are served by the backend API. */
export const getProfileImageUrl = (pictureURL: string | undefined): string => {
  if (!pictureURL) return "";
  try {
    const base = (envConfig.publicServerUrl || envConfig.imgStoreUrl || "").replace(/\/$/, "");
    if (!base) return pictureURL;

    const key = extractMediaKey(pictureURL);
    if (!key) return pictureURL;

    if (shouldServeMediaViaPayloadApi()) {
      return `${base}/api/media/file/${key}`;
    }

    const cdn = getCdnBaseUrl();
    return cdn ? `${cdn}/${key}` : `${base}/api/media/file/${key}`;
  } catch {
    return pictureURL;
  }
};

export const getImageUrl = (url: string) => {
  const raw = (url || "").trim();
  if (!raw) return "";

  if (/^https?:\/\//i.test(raw)) {
    let parsed: URL;
    try {
      parsed = new URL(raw);
    } catch {
      return raw;
    }

    const cdnBase = getCdnBaseUrl();
    if (cdnBase && raw.startsWith(`${cdnBase}/`)) {
      return raw;
    }

    let cdnHost = "";
    if (cdnBase) {
      try {
        cdnHost = new URL(cdnBase).hostname;
      } catch {
        cdnHost = "";
      }
    }

    // Match the URL host and path only. A proxy URL such as
    // /api/image-proxy?url=https://cdn/... still contains the CDN host in the query,
    // and rewriting that produced a 403 download ("No permissions" in Chrome).
    const pathHasMediaFile = parsed.pathname.includes(MEDIA_FILE_PATH);
    const hostIsCdn = Boolean(cdnHost) && parsed.hostname === cdnHost;
    if (pathHasMediaFile || hostIsCdn) {
      return resolveMediaKeyToUrl(raw);
    }

    return raw;
  }

  return resolveMediaKeyToUrl(raw);
};

/** Pick the best available thumbnail filename for list/card layouts. */
export function getArticleImageFilename(
  media: Pick<MediaType, "filename" | "sizes"> | null | undefined,
  preferred: ArticleImageSize = "ld",
): string {
  if (!media) return "";

  const fallbacks: ArticleImageSize[] = [preferred, "ld", "sd", "md", "hd"];
  for (const key of fallbacks) {
    const filename = media.sizes?.[key]?.filename;
    if (filename) return filename;
  }

  return media.filename || "";
}

/** Resolve a card/list thumbnail URL from Payload media. */
export function getArticleImageUrl(
  media: Pick<MediaType, "filename" | "sizes"> | null | undefined,
  preferred: ArticleImageSize = "ld",
): string {
  const filename = getArticleImageFilename(media, preferred);
  return filename ? getImageUrl(filename) : "";
}

/** Build responsive srcSet from Payload media size variants. */
export function getMediaSrcSet(
  media: Pick<MediaType, "sizes"> | null | undefined,
  sizeKeys: ArticleImageSize[] = ["ld", "sd", "md"],
): string {
  if (!media?.sizes) return "";

  const parts: string[] = [];
  for (const key of sizeKeys) {
    const size = media.sizes[key];
    const filename = size?.filename;
    if (!filename) continue;

    const url = getImageUrl(filename);
    const width = size?.width || SIZE_WIDTHS[key];
    if (url && width) {
      parts.push(`${url} ${width}w`);
    }
  }

  return parts.join(", ");
}

/** Resolve display URL from Payload media (sizes, filename, path, or url). */
export const getMediaImageUrl = (media: unknown): string => {
  if (typeof media === "string") {
    const raw = media.trim();
    return raw ? getImageUrl(raw) : "";
  }

  if (
    typeof media !== "object" ||
    media === null ||
    !("filename" in media || "path" in media || "url" in media || "sizes" in media)
  ) {
    return "";
  }

  const doc = media as MediaType;
  const sizeKeys: ArticleImageSize[] = ["md", "sd", "ld", "hd", "fullHD"];
  const candidates: (string | undefined | null)[] = [];

  for (const key of sizeKeys) {
    const size = doc.sizes?.[key];
    if (size?.filename) candidates.push(size.filename);
  }

  candidates.push(doc.filename, doc.path);

  for (const candidate of candidates) {
    const raw = (candidate ?? "").trim();
    if (!raw) continue;
    const resolved = getImageUrl(raw);
    if (resolved) return resolved;
  }

  if (doc.url) {
    return getImageUrl(doc.url);
  }

  return "";
};

/**
 * Ad creatives must use the original (or largest) asset — never the card
 * derivatives (`md`/`sd`/`ld`) that `getMediaImageUrl` prefers for news thumbs,
 * or wide banners can appear cropped / soft.
 */
export const getAdImageUrl = (adImage: unknown): string => {
  if (typeof adImage === "string") {
    const raw = adImage.trim();
    return raw ? getImageUrl(raw) : "";
  }

  if (typeof adImage !== "object" || adImage === null) return "";

  const doc = adImage as MediaType;
  const candidates: (string | undefined | null)[] = [
    doc.filename,
    doc.sizes?.hd?.filename,
    doc.sizes?.fullHD?.filename,
    doc.sizes?.md?.filename,
    doc.path,
    doc.url,
  ];

  for (const candidate of candidates) {
    const raw = (candidate ?? "").trim();
    if (!raw) continue;
    const resolved = getImageUrl(raw);
    if (resolved) return resolved;
  }

  return "";
};
